import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  UserCog, Plus, Edit, Trash2, Shield, Loader2, 
  Users, Briefcase, Eye, EyeOff, Crown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStaff = () => {
  const { getAuthHeader, user: currentUser } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'auditor'
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/staff`, getAuthHeader());
      setStaff(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/auth/register`, formData, getAuthHeader());
      toast.success(`${formData.role.charAt(0).toUpperCase() + formData.role.slice(1)} created successfully`);
      setShowCreateModal(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create staff member');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedStaff) return;

    setSaving(true);
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        address: formData.address || ''
      };
      
      // Only include password if it was changed
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(`${API}/admin/staff/${selectedStaff.id}`, updateData, getAuthHeader());
      toast.success('Staff member updated successfully');
      setShowEditModal(false);
      setSelectedStaff(null);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update staff member');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (staffMember) => {
    if (staffMember.id === currentUser?.id) {
      toast.error('You cannot delete your own account');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${staffMember.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/staff/${staffMember.id}`, getAuthHeader());
      toast.success('Staff member deleted successfully');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff member');
    }
  };

  const openEditModal = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      password: '',
      role: staffMember.role,
      address: staffMember.address || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'auditor',
      address: ''
    });
    setShowPassword(false);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-purple-100 text-purple-700"><Crown size={12} className="mr-1" /> Administrator</Badge>;
      case 'manager':
        return <Badge className="bg-blue-100 text-blue-700"><Briefcase size={12} className="mr-1" /> Manager</Badge>;
      case 'auditor':
        return <Badge className="bg-green-100 text-green-700"><Eye size={12} className="mr-1" /> Auditor</Badge>;
      default:
        return <Badge>{role}</Badge>;
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'admin':
        return 'Full access to all features including financials, payments, reporting, and staff management';
      case 'manager':
        return 'Access to User Management and Jobs Management. Cannot see client payments or financials';
      case 'auditor':
        return 'Access to User Management only. Can approve/deny users and manage Pro status';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-gray-500">Manage administrators, managers, and auditors</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="create-staff-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Staff Member
        </Button>
      </div>

      {/* Role Access Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border-purple-200 bg-purple-50">
          <div className="flex items-center gap-2 mb-2">
            <Crown size={18} className="text-purple-600" />
            <span className="font-semibold text-purple-900">Administrator</span>
          </div>
          <p className="text-sm text-purple-700">Full system access</p>
        </Card>
        <Card className="p-4 border-blue-200 bg-blue-50">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase size={18} className="text-blue-600" />
            <span className="font-semibold text-blue-900">Manager</span>
          </div>
          <p className="text-sm text-blue-700">Users + Jobs management</p>
        </Card>
        <Card className="p-4 border-green-200 bg-green-50">
          <div className="flex items-center gap-2 mb-2">
            <Eye size={18} className="text-green-600" />
            <span className="font-semibold text-green-900">Auditor</span>
          </div>
          <p className="text-sm text-green-700">Users management only</p>
        </Card>
      </div>

      {/* Staff List */}
      <div className="space-y-4">
        {staff.map((member) => (
          <Card key={member.id} className="p-4" data-testid={`staff-card-${member.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  member.role === 'admin' ? 'bg-purple-100' :
                  member.role === 'manager' ? 'bg-blue-100' : 'bg-green-100'
                }`}>
                  <UserCog size={24} className={
                    member.role === 'admin' ? 'text-purple-600' :
                    member.role === 'manager' ? 'text-blue-600' : 'text-green-600'
                  } />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{member.name}</h3>
                    {member.id === currentUser?.id && (
                      <Badge className="bg-gray-100 text-gray-600 text-xs">You</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {getRoleBadge(member.role)}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(member)}
                  data-testid={`edit-staff-${member.id}`}
                >
                  <Edit size={16} className="mr-1" />
                  Edit
                </Button>
                {member.id !== currentUser?.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(member)}
                    data-testid={`delete-staff-${member.id}`}
                  >
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Create Staff Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield size={24} className="text-purple-600" />
              Add New Staff Member
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                required
                data-testid="staff-name-input"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                required
                data-testid="staff-email-input"
              />
            </div>

            <div>
              <Label>Home Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, Dallas, TX 75201"
                data-testid="staff-address-input"
              />
              <p className="text-xs text-gray-400 mt-1">Required for auditors — they can only rate workers within 70 miles of this address.</p>
            </div>
            
            <div>
              <Label>Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Secure password"
                  required
                  data-testid="staff-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div>
              <Label>Role *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="staff-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auditor">Auditor</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">{getRoleDescription(formData.role)}</p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="save-staff-btn"
              >
                {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
                Create Staff Member
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit size={24} className="text-blue-600" />
              Edit Staff Member
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                required
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                required
              />
            </div>

            <div>
              <Label>Home Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, Dallas, TX 75201"
                data-testid="staff-edit-address-input"
              />
              <p className="text-xs text-gray-400 mt-1">Required for auditors — they can only rate workers within 70 miles of this address.</p>
            </div>
            
            <div>
              <Label>New Password (leave blank to keep current)</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div>
              <Label>Role *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auditor">Auditor</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">{getRoleDescription(formData.role)}</p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Edit size={16} className="mr-2" />}
                Update Staff Member
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedStaff(null);
                  resetForm();
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStaff;
